/**
 * @file decision-form/defs.ts
 * @description 驗證決定 ZHSR07 表單定義（一~五節），按驗證方案（scheme）產出。
 *
 * 來源模板：ZHSR07-2 產銷履歷（TAP）/ ZHSR07-3 有機（TOP）Rev7。
 * 一、二節 TAP/TOP 相同；三、四節條目/欄位不同；五節結論固定 8 項。
 * ECO 無專屬模板，回落 TAP。
 */
export const DECISION_CONCLUSIONS = [
    '同意發放',
    '不同意發放',
    '同意恢復',
    '暫時停止',
    '終止',
    '撤銷',
    '結束',
    '其他',
];
const YN = ['符合', '不符合'];
const YNA = ['符合', '不符合', '不適用'];
/**
 * 一、驗證過程規範性（TAP/TOP 相同，5 項）。
 * 客戶 2026-07 需求：各評定項改為多層「a) b) c)…」細項＋「？」問句形式，label 內含換行，
 * 前端以 white-space: pre-line 渲染。
 */
const PROCESS_SECTION = {
    key: 'process',
    title: '一、驗證過程規範性',
    items: [
        {
            key: 'p1',
            label: '1.申請受理與合約評審\n' +
                'a)合法經營的法律證明文件是否齊全？\n' +
                'b)合約評審時相關文件是否齊全、完整、有效？\n' +
                'c)驗證範圍是否屬被認證範圍？\n' +
                'd)申請者及環境條件資格是否符合驗證依據及相關規範及相關規範要求？',
            options: YN,
        },
        {
            key: 'p2',
            label: '2.驗證稽核委託及驗證稽核計畫\n' +
                'a)稽核小組的組成及人員資格是否符合要求？\n' +
                'b)驗證稽核計畫安排\n' +
                '　➤稽核人天安排是否符合規定要求？\n' +
                '　➤投入品、採收等關鍵過程稽核時間安排是否充分？\n' +
                '　➤稽核計畫是否得到受稽核方確認？',
            options: YN,
        },
        {
            key: 'p3',
            label: '3.現場稽核\n' +
                'a)稽核程序是否符合ZHCERT驗證流程及規定？\n' +
                'b)現場稽核與稽核計畫的符合性如何？\n' +
                'c)樣品的抽取之風險評估以及相關檢測/檢驗報告是否符合相關驗證要求的規定？\n' +
                'd)不符合項報告及採樣紀錄是否經受稽核方簽字確認？\n' +
                'e)有關受稽核方的顧客抱怨處理是否符合要求？',
            options: YN,
        },
        {
            key: 'p4',
            label: '4.稽核報告\n' +
                'a)稽核報告編寫是否完整、規範、符合要求？\n' +
                'b)稽核報告是否經相關方（稽核小組、報告批准人）確認？\n' +
                'c)實際稽核作業與稽核計畫之差異是否說明？\n' +
                'd)不符合項及矯正措施的驗證\n' +
                '　➤追蹤查核方式是否合理？\n' +
                '　➤不符合項事實描述是否清楚？\n' +
                '　➤不符合項矯正措施客觀證據是否充分？\n' +
                '　➤不符合項矯正時限是否符合要求？',
            options: YN,
        },
        {
            key: 'p5',
            label: '5.其他\n' +
                'a)上一年不符合項/改進建議（例行定期追蹤查驗/不定期追蹤查驗時）是否已追蹤驗證？\n' +
                'b)上一年的標章使用（例行定期追蹤查驗/不定期追蹤查驗時）是否符合要求？',
            options: YNA,
        },
    ],
};
/**
 * 二、集團驗證（TAP/TOP 相同）。
 * 客戶 2026-07 需求：拆為 2 個評定項並加「？」。
 */
const GROUP_SECTION = {
    key: 'group',
    title: '二、集團驗證（申請集團驗證適用）',
    items: [
        {
            key: 'g1',
            label: '1.※對集團驗證的稽核是否符合相關驗證規範的要求？',
            options: YNA,
        },
        {
            key: 'g2',
            label: '2.※組織的內部品質管理體系運行有效性？',
            options: YNA,
        },
    ],
};
/**
 * 三、決定結果 — TAP（產銷履歷）。
 * 客戶 2026-07 需求：各項結果加「？」問句形式並重新編號（1~7）；
 * r7「不推薦使用驗證標章」改為三選項（不適用／同意(不推薦使用驗證標章)／不同意此決定）。
 */
const RESULT_TAP = {
    key: 'result',
    title: '三、驗證決定結果',
    items: [
        {
            key: 'r1',
            label: '1.各項作業符合驗證依據及相關規範要求，並確實進行相關紀錄及查核作業？',
            options: YN,
        },
        { key: 'r2', label: '2.批次、編碼、追溯作業符合驗證依據及相關規範之規定？', options: YN },
        {
            key: 'r3',
            label: '3.生產之產銷履歷農產品，其採收總量應逐批於中央主管機關產銷履歷管理資訊系統登載，且是正確且完整之履歷資料？',
            options: YN,
        },
        {
            key: 'r4',
            label: '4.同時進行與非產銷履歷農產品之生產、加工、分裝、流通或儲藏作業時，作物、原料、資材及產品等批次，完全與非產銷履歷農產品區隔，有建立適當的辨識與標示系統，以防止交叉汙染或混雜？',
            options: YNA,
        },
        { key: 'r5', label: '5.產品藥物殘留檢測結果符合我國相關規定？', options: YN },
        {
            key: 'r6',
            label: '6.產銷履歷農產品標章或應標示事項符合相關規定？',
            options: ['符合', '不符合', '未使用標章'],
        },
        {
            key: 'r7',
            label: '7.以驗證名義申請之驗證批產品，經稽核小組稽核發現其生產、加工、分裝、流通或儲藏作業過程有不符合驗證依據及相關規範要求，稽核小組不推薦使用驗證標章：',
            options: ['不適用', '同意(不推薦使用驗證標章)', '不同意此決定'],
        },
    ],
};
/** 三、決定結果 — TOP（有機） */
const RESULT_TOP = {
    key: 'result',
    title: '三、驗證決定結果',
    items: [
        {
            key: 'o1',
            label: '各項作業符合驗證依據及相關規範要求，並確實進行相關紀錄及查核作業',
            options: YN,
        },
        { key: 'o2', label: '有機/有機轉型期農產品儲藏、運輸與配售符合驗證基準要求', options: YN },
        { key: 'o3', label: '有機/有機轉型期農產品包裝、標示及標章管理符合驗證標準規範', options: YN },
        { key: 'o4', label: '產品檢測結果符合驗證標準規範', options: YN },
        {
            key: 'o5',
            label: '有機完整性之相關作業紀錄、衛生管理記錄及單據憑證符合驗證基準要求',
            options: YN,
        },
        {
            key: 'o6',
            label: '若有平行生產之方式，符合驗證基準要求',
            options: ['符合', '不符合', '不適用'],
        },
        { key: 'o7', label: '稽核小組不推薦使用驗證標章', options: YNA },
    ],
};
/** 四、證書資訊欄位 — TAP */
const CERT_FIELDS_TAP = [
    { key: 'operatorName', label: '農產品經營者' },
    { key: 'responsiblePerson', label: '負責人' },
    { key: 'address', label: '地址' },
    { key: 'basisName', label: '驗證基準或作業規範之名稱' },
    { key: 'area', label: '驗證面積' },
    { key: 'productCategory', label: '農產品類別' },
    { key: 'productItems', label: '產品品項' },
    { key: 'verifyMethod', label: '驗證方式' },
    { key: 'landNumber', label: '驗證地號' },
    { key: 'firstAuditDate', label: '初次驗證日期' },
    { key: 'validPeriod', label: '證書有效期間' },
    { key: 'certNo', label: '證書字號' },
];
/** 四、證書資訊欄位 — TOP */
const CERT_FIELDS_TOP = [
    { key: 'operatorName', label: '農產品經營者名稱' },
    { key: 'responsiblePerson', label: '負責人' },
    { key: 'address', label: '農產品經營者地址' },
    { key: 'scheme', label: '驗證規範' },
    { key: 'basis', label: '驗證依據' },
    { key: 'option', label: '驗證選項' },
    { key: 'category', label: '驗證類別' },
    { key: 'productItems', label: '驗證品項' },
    { key: 'siteAddress', label: '驗證場所地址' },
    { key: 'firstAuditDate', label: '初次通過日期' },
    { key: 'validPeriod', label: '證書有效期間' },
    { key: 'certNo', label: '證書字號' },
];
const DEFS_TAP = {
    sections: [PROCESS_SECTION, GROUP_SECTION, RESULT_TAP],
    certFields: CERT_FIELDS_TAP,
    conclusions: [...DECISION_CONCLUSIONS],
};
const DEFS_TOP = {
    sections: [PROCESS_SECTION, GROUP_SECTION, RESULT_TOP],
    certFields: CERT_FIELDS_TOP,
    conclusions: [...DECISION_CONCLUSIONS],
};
/**
 * 依驗證方案取得表單定義。ECO 無專屬模板，回落 TAP。
 */
export function getDecisionFormDefs(scheme) {
    return scheme === 'TOP' ? DEFS_TOP : DEFS_TAP;
}
/**
 * 結論顯示文案（存儲值不變，僅供 UI 顯示）。
 * 客戶 2026-07 需求：發放/恢復類後綴「驗證證書」，停止/終止/撤銷/結束類後綴「驗證」。
 */
export function formatConclusionLabel(c) {
    switch (c) {
        case '同意發放':
            return '同意發放驗證證書';
        case '不同意發放':
            return '不同意發放驗證證書';
        case '同意恢復':
            return '同意恢復驗證證書';
        case '暫時停止':
            return '暫時停止驗證';
        case '終止':
            return '終止驗證';
        case '撤銷':
            return '撤銷驗證';
        case '結束':
            return '結束驗證';
        default:
            return c;
    }
}
